/*
  AuthContext - Supabase Authentication with Role-Based Access Control
  
  Provides:
  - Login/Logout/SignUp functions
  - User state management
  - Role-based access checking
  - Session persistence
*/

import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase, isSupabaseConfigured, SUPABASE_URL } from './supabaseClient';
import secureStorage from './secureStorage';

const AuthContext = createContext(null);

// Role hierarchy for permission checking
const ROLE_HIERARCHY = {
    admin: 4,
    manager: 3,
    equipier: 2,
    client: 1,
    visitor: 0
};

const VALID_ROLES = ['admin', 'manager', 'equipier', 'client'];

const IS_DEV = process.env.NODE_ENV !== 'production';

// Role can come from: 1) JWT app_metadata (after Supabase SQL sets raw_app_meta_data), 2) user_profiles table
function getRoleFromUser(user) {
    const fromJwt = user?.app_metadata?.user_role;
    if (fromJwt && VALID_ROLES.includes(fromJwt)) return fromJwt;
    return null;
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sessionReady, setSessionReady] = useState(false); // True when Supabase session is valid for API calls

    // Initialize auth state
    useEffect(() => {
        let isMounted = true;
        let timeoutId = null;
        if (IS_DEV) console.log('AuthProvider: Initializing...');

        const initAuth = async () => {
            try {
                if (!isSupabaseConfigured()) {
                    setLoading(false);
                    return;
                }

                // STEP 2: Immediately restore profile from secure storage for FAST UI
                // This allows dashboard to render while getSession() is still in progress
                const savedProfile = secureStorage.getItem('coq_profile');
                if (savedProfile && isMounted) {
                    try {
                        if (savedProfile && savedProfile.role && savedProfile.id && savedProfile.email) {
                            if (IS_DEV) console.log('AuthProvider: Restored profile from secure storage:', savedProfile);
                            setProfile(savedProfile);
                            if (timeoutId) clearTimeout(timeoutId);
                        }
                    } catch (e) {
                        console.error('Failed to parse saved profile:', e);
                        secureStorage.removeItem('coq_profile');
                    }
                }

                // STEP 3: Get session from Supabase (runs even if we already showed cached data)
                // This will update the user with a REAL session token for API calls
                if (IS_DEV) console.log('AuthProvider: Getting Supabase session...');
                const { data: { session }, error: sessionError } = await supabase.auth.getSession();

                if (sessionError) {
                    console.error('Session error:', sessionError);
                }

                if (!isMounted) return;

                if (session?.user) {
                    if (IS_DEV) console.log('Auth: Session restored for', session.user.email);
                    // Update with real user object (has valid auth token)
                    setUser(session.user);
                    setSessionReady(true); // Signal that API calls can now work

                    // Fetch fresh profile from database with proper error handling
                    try {
                        let profileData = null;
                        
                        // Try to get profile by user ID first
                        const { data: profileById, error: idError } = await supabase
                            .from('user_profiles')
                            .select('*')
                            .eq('id', session.user.id)
                            .maybeSingle();
                        
                        if (!idError && profileById) {
                            profileData = profileById;
                        } else if (idError && idError.code !== 'PGRST116') {
                            console.error('Profile by ID error:', idError);
                        }
                        
                        // If not found by ID, try by email (fallback for legacy data)
                        if (!profileData) {
                            const { data: profileByEmail, error: emailError } = await supabase
                                .from('user_profiles')
                                .select('*')
                                .eq('email', session.user.email)
                                .maybeSingle();
                            
                            if (!emailError && profileByEmail) {
                                profileData = profileByEmail;
                            } else if (emailError && emailError.code !== 'PGRST116') {
                                console.error('Profile by email error:', emailError);
                            }
                        }
                        
                        if (isMounted && profileData) {
                            const effectiveRole = getRoleFromUser(session.user) || profileData?.role || 'client';
                            const finalProfile = { ...profileData, role: effectiveRole, email: session.user.email };
                            setProfile(finalProfile);
                            secureStorage.setItem('coq_profile', finalProfile);
                        } else if (isMounted && !profileData) {
                            // Create basic profile if none exists
                            const basicProfile = { 
                                id: session.user.id, 
                                email: session.user.email, 
                                role: 'client' 
                            };
                            setProfile(basicProfile);
                            secureStorage.setItem('coq_profile', basicProfile);
                        }
                    } catch (e) {
                        console.error('Profile fetch error:', e);
                        if (isMounted) {
                            // Set minimal profile on error
                            const fallbackProfile = { 
                                id: session.user.id, 
                                email: session.user.email, 
                                role: 'client' 
                            };
                            setProfile(fallbackProfile);
                            secureStorage.setItem('coq_profile', fallbackProfile);
                        }
                    }
                } else {
                    // No session - user is not logged in
                    if (IS_DEV) console.log('Auth: No session found - clearing cache');
                    setUser(null);
                    setProfile(null);
                    secureStorage.removeItem('coq_profile');
                }
            } catch (err) {
                // Suppress AbortError - this happens during React StrictMode double-mounting
                if (err.name === 'AbortError') {
                    if (IS_DEV) console.log('Auth init aborted (expected during development)');
                    return;
                }
                console.error('Auth init error:', err);
            } finally {
                if (isMounted) {
                    setLoading(false);
                    if (timeoutId) clearTimeout(timeoutId);
                }
            }
        };

        // Failsafe timeout (reduced to 5s since we show cached data immediately)
        timeoutId = setTimeout(() => {
            if (isMounted) {
                if (IS_DEV) console.warn('Auth init timeout (5s) - forcing loading to false');
                setLoading(false);
            }
        }, 5000);

        initAuth();

        // Listen for auth changes - CRITICAL: Use setTimeout to avoid deadlock
        // See: https://supabase.com/docs/reference/javascript/auth-onauthstatechange
        let subscription = null;
        if (isSupabaseConfigured()) {
            const { data } = supabase.auth.onAuthStateChange((event, session) => {
                // SYNCHRONOUS ONLY - no await here to avoid deadlock!
                if (!isMounted) return;

                if (IS_DEV) console.log('Auth event:', event);

                if (!session?.user) {
                    if (event === 'SIGNED_OUT') {
                        setUser(null);
                        setProfile(null);
                        setSessionReady(false);
                        secureStorage.removeItem('coq_profile');
                    }
                    setLoading(false);
                    return;
                }

                // Set user immediately (synchronous)
                setUser(session.user);
                setSessionReady(true);

                // Restore from secure storage immediately (synchronous)
                const savedProfileRaw = secureStorage.getItem('coq_profile');
                if (savedProfileRaw) {
                    try {
                        const parsed = savedProfileRaw;
                        if (parsed?.role && parsed?.email === session.user.email) {
                            setProfile(parsed);
                        }
                    } catch (e) { /* ignore */ }
                }

                // DEFER async work using setTimeout to avoid deadlock!
                setTimeout(async () => {
                    if (!isMounted) return;
                    try {
                        if (IS_DEV) console.log('Fetching profile for:', session.user.email);

                        let profileData = null;
                        
                        // Try to get profile by user ID first
                        const { data: profileById, error: idError } = await supabase
                            .from('user_profiles')
                            .select('*')
                            .eq('id', session.user.id)
                            .maybeSingle();
                        
                        if (!idError && profileById) {
                            profileData = profileById;
                        } else if (idError && idError.code !== 'PGRST116') {
                            console.error('Profile by ID error:', idError);
                        }
                        
                        // If not found by ID, try by email (fallback for legacy data)
                        if (!profileData) {
                            const { data: profileByEmail, error: emailError } = await supabase
                                .from('user_profiles')
                                .select('*')
                                .eq('email', session.user.email)
                                .maybeSingle();
                            
                            if (!emailError && profileByEmail) {
                                profileData = profileByEmail;
                            } else if (emailError && emailError.code !== 'PGRST116') {
                                console.error('Profile by email error:', emailError);
                            }
                        }

                        if (isMounted && profileData) {
                            const roleFromJwt = getRoleFromUser(session.user);
                            const effectiveRole = roleFromJwt || profileData?.role || 'client';
                            const finalProfile = { ...profileData, role: effectiveRole, email: session.user.email };
                            setProfile(finalProfile);
                            secureStorage.setItem('coq_profile', finalProfile);
                        } else if (isMounted && !profileData) {
                            // Create basic profile if none exists
                            const basicProfile = { 
                                id: session.user.id, 
                                email: session.user.email, 
                                role: 'client' 
                            };
                            setProfile(basicProfile);
                            secureStorage.setItem('coq_profile', basicProfile);
                        }
                    } catch (err) {
                        console.error('Profile fetch error:', err);
                        if (isMounted) {
                            const fallbackProfile = { 
                                id: session.user.id, 
                                email: session.user.email, 
                                role: 'client' 
                            };
                            setProfile(fallbackProfile);
                            secureStorage.setItem('coq_profile', fallbackProfile);
                        }
                    }
                    if (isMounted) setLoading(false);
                }, 0); // setTimeout(fn, 0) defers execution to avoid Supabase deadlock
            });
            subscription = data?.subscription;
        }

        return () => {
            isMounted = false;
            if (timeoutId) clearTimeout(timeoutId);
            subscription?.unsubscribe();
        };
    }, []);


    // Sign up new user
    const signUp = async (email, password, fullName, phone = null) => {
        setError(null);
        try {
            if (!isSupabaseConfigured()) {
                throw new Error('Supabase is not configured');
            }

            const { data, error } = await supabase.auth.signUp({
                email,
                password,
                options: {
                    data: { full_name: fullName, phone }
                }
            });

            if (error) throw error;

            // Create user profile
            if (data.user) {
                // Check if profile exists first (race condition with trigger)
                const { data: existing } = await supabase
                    .from('user_profiles')
                    .select('id')
                    .eq('id', data.user.id)
                    .single();

                if (!existing) {
                    await supabase.from('user_profiles').insert([{
                        id: data.user.id,
                        email,
                        full_name: fullName,
                        phone,
                        role: 'client' // Default role for self-signup
                    }]);
                }
            }

            return { success: true, user: data.user };
        } catch (err) {
            setError(err.message);
            return { success: false, error: err.message };
        }
    };

    // Sign in with email/password (demo accounts removed for production)
    const signIn = async (email, password) => {
        setError(null);
        try {
            if (!isSupabaseConfigured()) {
                throw new Error('Supabase is not configured');
            }
            const timeoutPromise = new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Connexion timeout - Supabase ne répond pas. Vérifiez votre connexion internet ou si le projet Supabase est actif.')), 10000)
            );
            const signInPromise = supabase.auth.signInWithPassword({ email, password });
            const { data, error } = await Promise.race([signInPromise, timeoutPromise]);
            if (error) throw error;
            return { success: true, user: data.user };
        } catch (err) {
            setError(err.message);
            return { success: false, error: err.message };
        }
    };

    // Sign in with Google
    const signInWithGoogle = async () => {
        setError(null);
        try {
            const { error } = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: {
                    redirectTo: window.location.origin
                }
            });

            if (error) throw error;
            return { success: true };
        } catch (err) {
            setError(err.message);
            return { success: false, error: err.message };
        }
    };

    // Clear error
    const clearError = () => {
        setError(null);
    };

    // Sign out
    const signOut = async () => {
    try {
        // Clear React state first
        setUser(null);
        setProfile(null);

        if (isSupabaseConfigured()) {
            // Sign out from Supabase
            await supabase.auth.signOut();

            // Force clear ALL Supabase-related localStorage items
            // This ensures no session data persists
            const keysToRemove = [];
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key && (key.includes('supabase') || key.includes('sb-'))) {
                    keysToRemove.push(key);
                }
            }
            keysToRemove.forEach(key => localStorage.removeItem(key));
        }

        // Also clear our custom auth storage
        // Also clear our custom auth storage
        secureStorage.removeItem('coq_profile');

        return { success: true };
    } catch (err) {
        console.error('Logout error:', err);
        // Force local cleanup anyway
        setUser(null);
        setProfile(null);
        secureStorage.removeItem('coq_profile');
        return { success: true };
    }
};

// Check if user has required role
const hasRole = (requiredRole) => {
    if (!sessionReady || !profile) return false;
    const userLevel = ROLE_HIERARCHY[profile.role] || 0;
    const requiredLevel = ROLE_HIERARCHY[requiredRole] || 0;
    return userLevel >= requiredLevel;
};

// Check exact role match
const isRole = (role) => {
    if (!sessionReady) return false;
    return profile?.role === role;
};

// Get role display name
const getRoleDisplayName = (role) => {
    const names = {
        admin: 'Administrateur',
        manager: 'Gérant',
        equipier: 'Équipier',
        client: 'Client'
    };
    return names[role] || role;
};

// Create staff member (admin only) via Edge Function so service_role stays server-side
const createStaffMember = async (email, password, fullName, role, branch = null) => {
    if (!hasRole('admin')) {
        return { success: false, error: 'Permission refusée' };
    }

    try {
        if (!isSupabaseConfigured()) {
            throw new Error('Supabase not configured');
        }

        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) {
            return { success: false, error: 'Veuillez vous reconnecter' };
        }

        const url = `${SUPABASE_URL}/functions/v1/create-staff`;
        const res = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${session.access_token}`
            },
            body: JSON.stringify({ email, password, fullName, role, branch: branch || null })
        });
        const data = await res.json();

        if (!res.ok) {
            return { success: false, error: data.error || 'Erreur serveur' };
        }
        return { success: true, profile: data.profile };
    } catch (err) {
        return { success: false, error: err.message };
    }
};

// Get all staff members (admin/manager only)
const getStaffMembers = async () => {
    if (!hasRole('manager')) {
        return { success: false, error: 'Permission refusée' };
    }

    try {
        if (!isSupabaseConfigured()) {
            throw new Error('Supabase not configured');
        }

        const { data, error } = await supabase
            .from('user_profiles')
            .select('*')
            .in('role', ['admin', 'manager', 'equipier'])
            .order('created_at', { ascending: false });

        if (error) throw error;
        return { success: true, staff: data };
    } catch (err) {
        return { success: false, error: err.message };
    }
};

const value = {
    user,
    profile: sessionReady ? profile : null,
    loading,
    error,
    sessionReady, // True when Supabase session is valid for API calls
    isAuthenticated: !!user && sessionReady,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    hasRole,
    isRole,
    getRoleDisplayName,
    createStaffMember,
    getStaffMembers,
    setError,
    clearError
};

return (
    <AuthContext.Provider value={value}>
        {children}
    </AuthContext.Provider>
);
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

export default AuthContext;
